import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Space,
  message,
  Card,
  Statistic,
  Row,
  Col,
  Tag,
  Popconfirm,
  Tooltip,
  Typography,
  ConfigProvider,
  theme,
} from "antd";
import {
  Plus,
  RefreshCw,
  Edit,
  Trash2,
  Power,
  CheckCircle2,
  Key,
  Search,
  Shield,
  Clock,
  Server,
  Filter,
} from "lucide-react";
import moment from "moment";
import { useSelector } from "react-redux";
import ErrorBoundary from "../common/ErrorBoundary";
import apiService from "../../services/api";
import { isAdmin } from "../../utils/permissions";
import { formatDateTime } from "../../utils/formatters";
import "./TokenManagement.css";

const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography;

const TokenManagement = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentToken, setCurrentToken] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, disabled: 0 });
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");

  const user = useSelector((state) => state.auth.user);
  const isUserAdmin = isAdmin(user);

  const [form] = Form.useForm();
  const { token: themeToken } = theme.useToken();

  const loadTokens = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        page_size: pagination.pageSize,
        name: searchText || undefined,
        status: statusFilter || undefined,
        user_id: userFilter || undefined,
      };

      const requests = [apiService.tokens.getTokens(params)];
      if (isUserAdmin) {
        requests.push(apiService.tokens.getTokenStats());
      }

      const responses = await Promise.all(requests);
      const tokensResponse = responses[0];
      const statsResponse = isUserAdmin ? responses[1] : null;

      if (!tokensResponse.data.success) {
        throw new Error(tokensResponse.data.message || "获取Token列表失败");
      }
      if (isUserAdmin && statsResponse && !statsResponse.data.success) {
        throw new Error(statsResponse.data.message || "获取Token统计失败");
      }

      setTokens(tokensResponse.data.data?.list || []);
      setPagination((prev) => ({
        ...prev,
        total: tokensResponse.data.data?.total || 0,
      }));
      
      if (isUserAdmin && statsResponse) {
        const statsData = statsResponse.data.data || { total: 0, active: 0 };
        setStats({
          ...statsData,
          disabled: (statsData.total || 0) - (statsData.active || 0),
        });
      }
    } catch (err) {
      message.error(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        "加载Token失败",
      );
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, searchText, statusFilter, userFilter, isUserAdmin]);

  const loadUsers = useCallback(async () => {
    if (!isUserAdmin) return;
    try {
      const response = await apiService.users.getUsers({ page_size: 100 });
      if (response.data.success) {
        setUsers(response.data.data?.list || []);
      }
    } catch (err) {
      console.error("加载用户列表失败:", err);
    }
  }, [isUserAdmin]);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // 尝试解析旧格式的 JSON 数组（兼容旧的 demo data）
  const formatAllowedIps = (ipString) => {
    if (!ipString) return "";
    // 如果是 JSON 数组格式，则解析并转换为逗号分隔的字符串
    if (typeof ipString === "string" && ipString.trim().startsWith("[")) {
      try {
        const parsed = JSON.parse(ipString);
        if (Array.isArray(parsed)) {
          return parsed.join(", ");
        }
      } catch (e) {
        return ipString;
      }
    }
    // 确保规范化：去除多余空格并用逗号+空格连接
    return ipString.split(",").map(ip => ip.trim()).filter(ip => ip !== "").join(", ");
  };

  const handleSubmit = async (values) => {
    try {
      const submissionData = {
        ...values,
        expires_at: values.expires_at
          ? values.expires_at.toISOString()
          : undefined,
        allowed_ips: values.allowed_ips || "",
      };

      const apiCall = currentToken
        ? apiService.tokens.updateToken(currentToken.id, submissionData)
        : apiService.tokens.createToken(submissionData);

      const response = await apiCall;
      if (!response.data.success) {
        throw new Error(
          response.data.message || `${currentToken ? "更新" : "创建"}Token失败`,
        );
      }

      message.success(
        `Token ${currentToken ? "更新" : "创建"}成功${!currentToken && response.data.data?.token ? "：" + response.data.data.token : ""}`,
      );
      setShowModal(false);
      form.resetFields();
      setCurrentToken(null);
      loadTokens();
    } catch (err) {
      message.error(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message,
      );
    }
  };

  const handleDelete = async (tokenId) => {
    try {
      const response = await apiService.tokens.deleteToken(tokenId);
      if (!response.data.success) {
        throw new Error(response.data.message || "删除Token失败");
      }
      message.success("Token删除成功");
      loadTokens();
    } catch (err) {
      message.error(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message,
      );
    }
  };

  const handleToggleStatus = async (token) => {
    const newStatus = token.status === "active" ? "disabled" : "active";
    try {
      const response = await apiService.tokens.updateTokenStatus(
        token.id,
        newStatus,
      );
      if (!response.data.success) {
        throw new Error(response.data.message || "更新Token状态失败");
      }
      message.success(`Token已${newStatus === "active" ? "启用" : "禁用"}`);
      loadTokens();
    } catch (err) {
      message.error(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message,
      );
    }
  };

  const openModal = (token = null) => {
    if (token) {
      setCurrentToken(token);
      form.setFieldsValue({
        name: token.name,
        description: token.description || "",
        permission: token.permission,
        allowed_ips: formatAllowedIps(token.allowed_ips),
        expires_at: token.expires_at ? moment(token.expires_at) : null,
        user_id: token.user_id,
      });
    } else {
      setCurrentToken(null);
      form.resetFields();
    }
    setShowModal(true);
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    loadTokens();
  };

  const columns = [
    ...(isUserAdmin ? [{
      title: "关联用户",
      dataIndex: "user_id",
      key: "user_username",
      width: 120,
      render: (userId, record) => {
        const username = record.user?.username || users.find(u => u.id === userId)?.username;
        return <span>{username || `ID: ${userId}`}</span>;
      },
    }] : []),
    {
      title: "Token 信息",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div className="token-info-cell">
          <div className="token-name">
            <Key className="token-icon" size={16} />
            <span className="token-name-text">{text}</span>
          </div>
          {record.description && (
            <Text type="secondary" className="token-description">
              {record.description}
            </Text>
          )}
          <Tooltip title="点击复制完整Token">
            <Text
              code
              copyable={{ text: record.token }}
              className="token-preview"
            >
              {record.token.substring(0, 12)}...
            </Text>
          </Tooltip>
        </div>
      ),
    },
    {
      title: "权限 & 安全",
      dataIndex: "permission",
      key: "permission",
      width: 200,
      render: (permission, record) => (
        <div className="permission-cell">
          <div className="permission-tags">
            <Tag className={`permission-tag ${permission}`}>
              {permission === "read_only" && "只读"}
              {permission === "read_write" && "读写"}
              {permission === "full" && "完全控制"}
            </Tag>
          </div>
          {formatAllowedIps(record.allowed_ips) && (
            <div className="ip-restriction">
              <Shield className="ip-icon" size={14} style={{ color: "var(--color-warning)" }} />
              <Tooltip title={formatAllowedIps(record.allowed_ips)}>
                <Text type="secondary" className="ip-text" style={{ fontSize: "var(--text-xs)" }}>
                  {formatAllowedIps(record.allowed_ips)}
                </Text>
              </Tooltip>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag className={`status-tag ${status}`}>
          {status === "active" ? "启用" : "禁用"}
        </Tag>
      ),
    },
    {
      title: "使用情况",
      key: "usage",
      width: 120,
      render: (_, record) => (
        <div className="usage-cell">
          <div className="usage-count">
            <Server className="usage-icon" size={16} />
            <span className="usage-number">{record.usage_count || 0}</span>
          </div>
          {record.last_used_at && (
            <Text type="secondary" className="last-used">
              {formatDateTime(record.last_used_at)}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "有效期",
      dataIndex: "expires_at",
      key: "expires_at",
      render: (date) => {
        if (!date) {
          return (
            <div className="expires-cell">
              <Text className="expires-forever">永不过期</Text>
            </div>
          );
        }
        const isExpired = moment(date).isBefore(moment());
        const isExpiringSoon = moment(date).diff(moment(), "days") < 7;
        return (
          <div className="expires-cell">
            <Clock className="expires-icon" size={14} />
            <Text
              className={
                isExpired
                  ? "expires-expired"
                  : isExpiringSoon
                    ? "expires-soon"
                    : "expires-normal"
              }
            >
              {formatDateTime(date)}
            </Text>
          </div>
        );
      },
    },
    {
      title: "操作",
      key: "actions",
      width: 280,
      render: (_, record) => (
        <Space size="small" className="action-buttons">
          <Tooltip title="编辑">
            <Button
              className="action-btn edit-btn"
              icon={<Edit size={14} />}
              onClick={() => openModal(record)}
              size="small"
            >
              编辑
            </Button>
          </Tooltip>
          <Tooltip title={record.status === "active" ? "禁用" : "启用"}>
            <Popconfirm
              title={`确定要${record.status === "active" ? "禁用" : "启用"}此Token吗？`}
              onConfirm={() => handleToggleStatus(record)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                className={`action-btn ${record.status === "active" ? "disable-btn" : "enable-btn"}`}
                icon={<Power size={14} />}
                size="small"
              >
                {record.status === "active" ? "禁用" : "启用"}
              </Button>
            </Popconfirm>
          </Tooltip>
          <Tooltip title="删除">
            <Popconfirm
              title="确定要删除此Token吗？此操作不可恢复！"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button
                className="action-btn delete-btn"
                icon={<Trash2 size={14} />}
                danger
                size="small"
              >
                删除
              </Button>
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <ErrorBoundary>
      <div className="token-management">
        {/* 页面头部 */}
        <div className="page-header">
          <div className="header-content">
            <Title level={2} className="page-title">
              <Key className="title-icon" size={32} />
              API Token 管理
            </Title>
            <Text className="page-subtitle">
              创建和管理 API 访问令牌，控制访问权限和安全策略
            </Text>
          </div>
          <Button
            type="primary"
            size="large"
            icon={<Plus size={18} />}
            onClick={() => openModal()}
            className="create-button"
          >
            创建新 Token
          </Button>
        </div>

        {/* 统计卡片 (仅管理员可见) */}
        {isUserAdmin && (
        <div className="stats-container">
          <Row gutter={[20, 20]}>
            <Col xs={24} sm={12} md={12} lg={8}>
              <Card className="stat-card stat-total">
                <div className="stat-content">
                  <div className="stat-icon-wrapper stat-total-icon">
                    <Key size={20} />
                  </div>
                  <div className="stat-info">
                    <Text type="secondary" className="stat-label">
                      Token 总数
                    </Text>
                    <div className="stat-value">{stats.total}</div>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={12} lg={8}>
              <Card className="stat-card stat-active">
                <div className="stat-content">
                  <div className="stat-icon-wrapper stat-active-icon">
                    <CheckCircle2 size={20} />
                  </div>
                  <div className="stat-info">
                    <Text type="secondary" className="stat-label">
                      活跃 Token
                    </Text>
                    <div className="stat-value">{stats.active}</div>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={12} lg={8}>
              <Card className="stat-card stat-disabled">
                <div className="stat-content">
                  <div className="stat-icon-wrapper stat-disabled-icon">
                    <Power size={20} />
                  </div>
                  <div className="stat-info">
                    <Text type="secondary" className="stat-label">
                      已禁用
                    </Text>
                    <div className="stat-value">{stats.disabled}</div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
        )}

        {/* Token列表 */}
        <div className="filters-toolbar">
          <div className="filter-group left">
            <div className="filter-item search-wrapper">
              <Input
                placeholder="搜索 Token 名称"
                prefix={<Search size={16} className="text-muted" />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={handleSearch}
                className="premium-input search-input"
                allowClear
              />
            </div>
            {isUserAdmin && (
            <div className="filter-item select-wrapper">
              <Select
                placeholder="按用户筛选"
                value={userFilter || undefined}
                onChange={(value) => {
                  setUserFilter(value || "");
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                className="premium-select user-select"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {users.map((user) => (
                  <Option key={user.id} value={user.id}>
                    {user.username}
                  </Option>
                ))}
              </Select>
            </div>
            )}
            <div className="filter-item select-wrapper">
              <Select
                placeholder="所有状态"
                value={statusFilter || undefined}
                onChange={(value) => {
                  setStatusFilter(value || "");
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                className="premium-select status-select"
                allowClear
              >
                <Option value="active">启用</Option>
                <Option value="disabled">禁用</Option>
              </Select>
            </div>
            <div className="filter-item action-wrapper">
              <Space size="small">
                <Button
                  type="primary"
                  onClick={handleSearch}
                  className="filter-btn primary-btn"
                >
                  搜索
                </Button>
                <Button
                  icon={<RefreshCw size={14} />}
                  onClick={() => {
                    setSearchText("");
                    setStatusFilter("");
                    setUserFilter("");
                    setPagination((prev) => ({ ...prev, current: 1 }));
                    loadTokens();
                  }}
                  className="filter-btn reset-btn"
                >
                  重置
                </Button>
              </Space>
            </div>
          </div>
          <div className="filter-group right">
            <Button
              type="text"
              icon={<RefreshCw size={16} />}
              onClick={loadTokens}
              className="refresh-icon-btn"
            />
          </div>
        </div>

        <div className="table-container">
          <Table
            columns={columns}
            dataSource={tokens}
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            onChange={handleTableChange}
            rowKey="id"
            className="tokens-table"
          />
        </div>

        {/* 创建/编辑Modal */}
        <Modal
          title={
            <div className="modal-title">
              <Key size={20} />
              <span>{currentToken ? "编辑 Token" : "创建新 Token"}</span>
            </div>
          }
          open={showModal}
          onCancel={() => {
            setShowModal(false);
            form.resetFields();
            setCurrentToken(null);
          }}
          footer={null}
          className="token-modal"
          width={640}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              permission: "read_only",
              user_id: 1,
            }}
            className="token-form"
          >
            <Form.Item
              label="Token 名称"
              name="name"
              rules={[{ required: true, message: "请输入Token名称" }]}
              className="form-item-spaced"
            >
              <Input
                placeholder="例如：生产环境 API Token"
                className="form-input"
                size="large"
              />
            </Form.Item>

            <Form.Item
              label="描述"
              name="description"
              className="form-item-spaced"
            >
              <TextArea
                rows={3}
                placeholder="描述此 Token 的用途和使用场景"
                className="form-input"
              />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="权限级别"
                  name="permission"
                  rules={[{ required: true, message: "请选择权限级别" }]}
                  className="form-item-spaced"
                >
                  <Select size="large" className="form-input">
                    <Option value="read_only">只读</Option>
                    <Option value="read_write">读写</Option>
                    <Option value="full">完全控制</Option>
                  </Select>
                </Form.Item>
              </Col>
              {isUserAdmin && (
              <Col span={12}>
                <Form.Item
                  label="关联用户"
                  name="user_id"
                  rules={[{ required: true, message: "请选择关联用户" }]}
                  className="form-item-spaced"
                >
                  <Select
                    size="large"
                    className="form-input"
                    placeholder="选择关联用户"
                    showSearch
                    optionFilterProp="children"
                  >
                    {users.map((user) => (
                      <Option key={user.id} value={user.id}>
                        {user.username}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              )}
            </Row>

            <Form.Item
              label="允许的 IP 地址"
              name="allowed_ips"
              extra="多个 IP 请用逗号分隔（如：203.0.113.0/24, 127.0.0.1），留空表示允许所有 IP"
              className="form-item-spaced"
            >
              <Input
                placeholder="例如：203.0.113.0/24, 127.0.0.1"
                className="form-input"
              />
            </Form.Item>

            <Form.Item
              label="过期时间"
              name="expires_at"
              extra="留空表示永不过期"
              className="form-item-spaced"
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm:ss"
                style={{ width: "100%" }}
                placeholder="选择过期时间"
                size="large"
                className="form-input"
              />
            </Form.Item>

            <Form.Item className="form-actions">
              <Space size="large">
                <Button
                  size="large"
                  onClick={() => {
                    setShowModal(false);
                    form.resetFields();
                    setCurrentToken(null);
                  }}
                  className="cancel-button"
                >
                  取消
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  className="submit-button"
                >
                  {currentToken ? "更新 Token" : "创建 Token"}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </ErrorBoundary >
  );
};

export default TokenManagement;
